import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { archiveAction as archive } from "../../../../../redux/reducers/Archives";
import { Archive } from "../../../../../redux/reducers/Archives/interface";
import Order from "../../../../../services/Order";
import {
	Schema,
	getOrderPassengerPoints,
	prepareOrderByForm,
} from "../../../../../redux/constants/OrdersPage/order";
import { useTypedDispatch } from "../../../../../redux/store";
import calcRoute from "../../../../../redux/services/Map/calcRoute";
import { revertOrder } from "../../../../../redux/services/Order/revertOrder";
import OrderStatus from "../../../../../types/OrderStatus";

import useArchiveOrders from "./useArchiveOrders";
import useArchiveSwisher from "./useArchiveSwitcher";

/**
 * Archive edit: editing a closed order reuses `OrderDao.revert`
 * (`Edit`) and re-closes with `status: "executed"` (`Save`/`Cancel`), rather
 * than introducing a new order status. `workingOrderId` is normally the
 * same id as the order that was reverted; the backend creates a copy
 * (`isCopy`) for closed orders that never reached an `executingStage`
 * (legacy rows) — `applyRevertResult` then retargets the modal at that copy.
 */
type EditSession = {
	originalOrderId: number;
	workingOrderId: number;
	isCopy: boolean;
	/** Cost at the moment the edit session became editable (post-revert).
	 * Used by the Footer to show the skip-balance-delta checkbox only when
	 * the dispatcher actually changed the price during this session. */
	preEditCost: number;
};

/**
 * `params` / `editSession` must be shared across every `useOrderUpdate()`
 * call site (Footer `Edit`/`Cancel` vs InfoModalOrder `Save`/`onFormChange`).
 * Local `useState` per hook instance left Save blind to the session that
 * Edit had just opened, so the close+delta path never ran.
 */
let sharedParams: Schema | null = null;
let sharedEditSession: EditSession | null = null;
const sharedListeners = new Set<() => void>();

const emitShared = () => {
	sharedListeners.forEach((listener) => listener());
};

const subscribeShared = (listener: () => void) => {
	sharedListeners.add(listener);
	return () => {
		sharedListeners.delete(listener);
	};
};

type UseOrderUpdate = {
	loading: boolean;
	order: Order.Model | null;
	setRouteToActiveOrder: (data: Archive.Orders.Route) => void;
	setParams: Dispatch<SetStateAction<Schema | null>>;
	addParams: (schema: Schema, passenger?: number) => void;
	setDataToOrder: (
		schema: Schema,
		isAdd?: boolean,
		passenger?: number,
	) => void;
	revertActiveOrder: (id: number) => Promise<void>;
	/** Whether the active order is eligible to be returned to air (same
	 * condition as the "revert to air" button). */
	canRevertOrder: boolean;
	/** Whether the "Edit" button on a closed order should be enabled.
	 * Same gate as the "revert to air" button (`canRevertOrder`). */
	canEditClosedOrder: boolean;
	/** Non-null while an archive-edit revert has happened and hasn't been
	 * committed (`Save`) or discarded (`Cancel`) yet. */
	editSession: EditSession | null;
	/** `Edit` on a closed order: revert in place, then the caller enables
	 * the form. Returns whether the session actually started - the form
	 * must stay read-only on failure. No-op (returns `true`) for orders
	 * that aren't closed. */
	startEditSession: () => Promise<boolean>;
	/** `Save`: persists the pending form changes and re-closes the order
	 * (`status: "executed"`) if an edit session is active; otherwise a
	 * plain `Order.update` for a non-archived order. Returns whether it
	 * fully succeeded - on failure the session (if any) is kept so the
	 * dispatcher can retry from the same EXECUTING state. */
	commitEdit: () => Promise<boolean>;
	/** `Cancel` / closing the modal / navigating away mid-session: closes
	 * the order back out (`status: "executed"`, no field changes) without
	 * consuming the pending form edits. No-op if there's no active
	 * session. */
	abortEdit: () => Promise<boolean>;
};

/**
 *  `useArchiveOrderUpdate`
 */
export const useOrderUpdate = (): UseOrderUpdate => {
	const dispatch = useTypedDispatch();
	const {
		setOrderLoading,
		orderLoading: { isOrder },
	} = useArchiveSwisher();
	const {
		orders: { order, activeOrderId },
	} = useArchiveOrders();

	const [params, setParamsState] = useState<Schema | null>(sharedParams);
	const [editSession, setEditSessionState] = useState<EditSession | null>(
		sharedEditSession,
	);

	useEffect(() => {
		const sync = () => {
			setParamsState(sharedParams);
			setEditSessionState(sharedEditSession);
		};
		sync();
		return subscribeShared(sync);
	}, []);

	const setParams = useCallback<UseOrderUpdate["setParams"]>((value) => {
		sharedParams =
			typeof value === "function" ? value(sharedParams) : value;
		setParamsState(sharedParams);
		emitShared();
	}, []);

	const setEditSession = useCallback(
		(value: SetStateAction<EditSession | null>) => {
			sharedEditSession =
				typeof value === "function"
					? value(sharedEditSession)
					: value;
			setEditSessionState(sharedEditSession);
			emitShared();
		},
		[],
	);

	const addParams = useCallback<UseOrderUpdate["addParams"]>(
		(schema) => {
			if (!order || !activeOrderId) return;
			setParams(schema);
		},
		[activeOrderId, order, setParams],
	);

	const setRouteToActiveOrder = useCallback<
		UseOrderUpdate["setRouteToActiveOrder"]
	>(
		(data: Archive.Orders.Route) => {
			dispatch(archive.orders.orders.setRouteToActiveOrder(data));
		},
		[dispatch],
	);

	// Same condition that enables the "revert to air" button (`LeftPart`), so
	// both the revert button and the edit-a-closed-order flow stay in sync.
	const canRevertOrder = useMemo<boolean>(() => {
		if (!order || !activeOrderId) return false;

		if (
			order.status === OrderStatus.CLOSED &&
			!order?.executorToOrder?.length
		) {
			return false;
		}

		if (order.status === OrderStatus.CLOSED) {
			return true;
		}

		if (!order?.executingStage && order?.closedAt) {
			return true;
		}

		return false;
	}, [
		order?.status,
		order?.executorToOrder?.length,
		order?.executingStage,
		order?.closedAt,
		activeOrderId,
	]);

	// Edit and "revert to air" share one gate so the two buttons never
	// disagree about whether a closed order is actionable. If revert
	// creates a copy (no `executingStage`), `startEditSession` /
	// `applyRevertResult` already retarget the modal at that copy.
	const canEditClosedOrder = canRevertOrder;

	const revertActiveOrder = useCallback<UseOrderUpdate["revertActiveOrder"]>(
		async (id) => {
			if (!canRevertOrder || !activeOrderId) return;
			dispatch(revertOrder({ id: id || activeOrderId }));
			dispatch(
				archive.orders.orders.removeOrder({ id: id || activeOrderId }),
			);
			dispatch(archive.orders.orders.setOrder(null));
		},
		[dispatch, canRevertOrder, activeOrderId],
	);

	// Only the route of the last points change is drawn: an older answer
	// arriving late would put back the previous track (dev check F8).
	const routeRequestRef = useRef(0);

	const setDataToOrder = useCallback<UseOrderUpdate["setDataToOrder"]>(
		(schema, isAdd = true, passenger = 0) => {
			const points = schema.points.map((item) => item?.coordinates);
			const passengerPoints = schema.points?.map((data) => ({
				feature: data,
				coordinates: data.coordinates,
			}));

			dispatch(
				archive.orders.orders.setPointsToActiveOrder({
					passenger,
					points: passengerPoints,
				}),
			);
			routeRequestRef.current += 1;
			const routeRequest = routeRequestRef.current;
			dispatch(
				calcRoute(points, (result) => {
					if (routeRequest !== routeRequestRef.current) return;
					if (result) setRouteToActiveOrder(result);
				}),
			);
			if (isAdd) addParams(schema);
		},
		[dispatch, addParams, setRouteToActiveOrder],
	);

	const applyRevertResult = useCallback(
		async (
			workingOrderId: number,
			originalOrderId: number,
			previous?: Order.Model | null,
		): Promise<Order.Model | null> => {
			const refreshed = await Order.getById(workingOrderId, {
				disableSanitize: false,
			});
			if (!refreshed) return null;

			// `getById` after revert sometimes omits nested
			// `taxiService.company`, which zeroes `form.companyID` and
			// crashes OrderForm (`useAddPointRow` → `.taxiServices`).
			const next: Order.Model = {
				...refreshed,
				taxiService:
					refreshed.taxiService?.company != null
						? refreshed.taxiService
						: previous?.taxiService ?? refreshed.taxiService,
				executorToOrder: refreshed.executorToOrder?.length
					? refreshed.executorToOrder
					: previous?.executorToOrder ?? refreshed.executorToOrder,
			};

			dispatch(archive.orders.orders.setOrder(next));
			dispatch(archive.orders.orders.updateOrder(next));

			// The backend created a copy instead of reverting the
			// original order in place: drop the now-stale original
			// from the archive list and point the active order id
			// at the copy, so later interactions (Save/Cancel,
			// re-editing, etc.) target the right record.
			if (workingOrderId !== originalOrderId) {
				dispatch(
					archive.orders.orders.removeOrder({
						id: originalOrderId,
					}),
				);
				dispatch(
					archive.orders.orders.setActiveOrderId(workingOrderId),
				);
			}

			return next;
		},
		[dispatch],
	);

	const startEditSession = useCallback<
		UseOrderUpdate["startEditSession"]
	>(async () => {
		if (!order || !activeOrderId) return false;

		// Editing a non-closed order needs no revert/close dance at all.
		if (order.status !== OrderStatus.CLOSED) return true;

		if (!canEditClosedOrder) return false;

		setOrderLoading(true);
		try {
			const reverted = await dispatch(
				revertOrder({ id: order.id, archiveEdit: true }),
			);
			if (!reverted?.id) {
				setOrderLoading(false);
				return false;
			}

			const workingOrderId = reverted.id;
			const isCopy = workingOrderId !== order.id;

			const refreshed = await applyRevertResult(
				workingOrderId,
				order.id,
				order,
			);
			if (!refreshed) {
				setOrderLoading(false);
				return false;
			}

			// Baseline the checkbox against the post-revert cost so it only
			// appears after the dispatcher actually changes the price (not
			// merely because revert itself refreshed the cost).
			setEditSession({
				originalOrderId: order.id,
				workingOrderId,
				isCopy,
				preEditCost: Number(refreshed.cost?.value ?? 0),
			});

			setOrderLoading(false);
			return true;
		} catch (error) {
			setOrderLoading(false);
			return false;
		}
	}, [
		order,
		activeOrderId,
		canEditClosedOrder,
		dispatch,
		setOrderLoading,
		applyRevertResult,
	]);

	/** After archive-edit close, keep the executor on the Redux order even
	 * if `getById` omitted `executorToOrder` (e.g. stale `hasExecutor:
	 * false`). Without an executor, `canRevertOrder` stays false and the
	 * dispatcher can't edit/revert a second time. */
	const applyClosedOrderResult = useCallback(
		(refreshed: Order.Model | null, previous: Order.Model) => {
			const previousExecutor = previous.executorToOrder;
			const next: Order.Model = refreshed
				? {
						...refreshed,
						executorToOrder: refreshed.executorToOrder?.length
							? refreshed.executorToOrder
							: previousExecutor,
				  }
				: {
						...previous,
						status: OrderStatus.CLOSED,
						closedAt: previous.closedAt || new Date().toISOString(),
				  };

			dispatch(archive.orders.orders.setOrder(next));
			dispatch(archive.orders.orders.updateOrder(next));
		},
		[dispatch],
	);

	const commitEdit = useCallback<UseOrderUpdate["commitEdit"]>(async () => {
		if (!params || !order) return false;

		const targetOrderId = editSession?.workingOrderId ?? order.id;

		setOrderLoading(true);
		try {
			const passenger = order?.passengers.at(0);

			// Live point edits write into Redux immediately (`setDataToOrder`)
			// but `params` can lag (debounced `onFormChange`, cost-driven form
			// reset). Prefer whichever stop list is longer so Save never
			// re-closes with the pre-edit route / cost.
			const paramsPointCount = (params.points || []).filter((item) => {
				if (!item) return false;
				if (!item.coordinates?.lat) return false;
				if (!item.coordinates?.lng) return false;
				return true;
			}).length;
			const orderPoints = getOrderPassengerPoints(order);
			const formForSave: Schema = {
				...params,
				points:
					orderPoints.length > paramsPointCount
						? orderPoints
						: params.points,
				price:
					params.price != null
						? params.price
						: Number(order.cost?.value ?? 0),
			};

			const payload: Order.Model.Modified = prepareOrderByForm(
				formForSave,
				passenger?.customer?.id,
			);

			if (!editSession) {
				// Plain edit of a non-archived order - unchanged behaviour.
				await Order.update({ ...payload, id: targetOrderId });
				setOrderLoading(false);
				return true;
			}

			// `!isOwn` orders only get a fresh cost from `close()` if the
			// order was actually updated with `refreshCost` first - see
			// `close.ts` recalculation gate on the backend.
			const updated = await Order.update(
				{ ...payload, id: targetOrderId },
				{ refreshCost: true, dispatcherModal: true },
			);
			if (!updated) {
				// Stays EXECUTING with the session intact - Save can be
				// retried without reverting again.
				setOrderLoading(false);
				return false;
			}

			const closed = await Order.close(targetOrderId, {
				status: "executed",
			});
			if (!closed) {
				// Update went through, close didn't - stays EXECUTING;
				// retry Save (re-sends the same update, then close again).
				setOrderLoading(false);
				return false;
			}

			const refreshed = await Order.getById(targetOrderId, {
				disableSanitize: false,
			});
			// Prefer the post-close fetch; if it comes back thin on route /
			// cost / points, keep what `update({ refreshCost })` already
			// returned so the modal doesn't snap back to the pre-edit path.
			const merged =
				refreshed != null
					? {
							...refreshed,
							passengers:
								(refreshed.passengers?.[0]?.points?.length ??
									0) >=
								(updated.passengers?.[0]?.points?.length ?? 0)
									? refreshed.passengers
									: updated.passengers ?? refreshed.passengers,
							route: refreshed.route?.segments?.length
								? refreshed.route
								: updated.route ?? refreshed.route,
							cost:
								refreshed.cost?.value != null
									? refreshed.cost
									: updated.cost ?? refreshed.cost,
					  }
					: updated;
			applyClosedOrderResult(merged, order);

			setEditSession(null);
			setOrderLoading(false);
			return true;
		} catch (error) {
			setOrderLoading(false);
			return false;
		}
	}, [
		params,
		order,
		editSession,
		dispatch,
		setOrderLoading,
		applyClosedOrderResult,
	]);

	const abortEdit = useCallback<UseOrderUpdate["abortEdit"]>(async () => {
		if (!editSession) return true;

		setOrderLoading(true);
		try {
			const closed = await Order.close(editSession.workingOrderId, {
				status: "executed",
			});
			if (!closed) {
				setOrderLoading(false);
				return false;
			}

			const refreshed = await Order.getById(editSession.workingOrderId, {
				disableSanitize: false,
			});
			if (order) {
				applyClosedOrderResult(refreshed, order);
			} else if (refreshed) {
				dispatch(archive.orders.orders.setOrder(refreshed));
				dispatch(archive.orders.orders.updateOrder(refreshed));
			}

			setEditSession(null);
			setOrderLoading(false);
			return true;
		} catch (error) {
			setOrderLoading(false);
			return false;
		}
	}, [
		editSession,
		order,
		dispatch,
		setOrderLoading,
		applyClosedOrderResult,
	]);

	// An active session left the order EXECUTING (not CLOSED) on the
	// server; a hard refresh/tab close would leave it that way (an "orphan"
	// until a dispatcher notices and re-closes it manually or, later,
	// phase 4's TTL recovery). Warn before that happens.
	useEffect(() => {
		if (!editSession) return undefined;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			// eslint-disable-next-line no-param-reassign
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [editSession]);

	return useMemo<UseOrderUpdate>(
		() => ({
			order,
			loading: isOrder,
			setParams,
			addParams,
			setDataToOrder,
			setRouteToActiveOrder,
			revertActiveOrder,
			canRevertOrder,
			canEditClosedOrder,
			editSession,
			startEditSession,
			commitEdit,
			abortEdit,
		}),
		[
			order,
			isOrder,
			addParams,
			setParams,
			setDataToOrder,
			setRouteToActiveOrder,
			revertActiveOrder,
			canRevertOrder,
			canEditClosedOrder,
			editSession,
			startEditSession,
			commitEdit,
			abortEdit,
		],
	);
};

export default useOrderUpdate;
